import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { api } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ChevronRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { toast } from 'sonner';

export function ServiceCatalogPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filterApp, setFilterApp] = useState('');
  const [filterTitle, setFilterTitle] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [offcanvasTitle, setOffcanvasTitle] = useState('');
  const [offcanvasLink, setOffcanvasLink] = useState('');

  const { data: definitionsData, isLoading } = useQuery({
    queryKey: ['service-definitions'],
    queryFn: () => api.get('/api/services/definition/list').then((r) => r.data),
  });

  const { data: appDefinitionsData } = useQuery({
    queryKey: ['app-definitions'],
    queryFn: () => api.get('/api/apps/definition/list').then((r) => r.data),
  });

  // API returns a flat array of service definitions
  const allDefinitions = Array.isArray(definitionsData)
    ? definitionsData
    : (definitionsData?.query?.data ?? []);
  const appDefinitions = Array.isArray(appDefinitionsData)
    ? appDefinitionsData
    : (appDefinitionsData?.query?.data ?? []);

  // Client-side filtering
  const definitions = allDefinitions.filter((def: any) => {
    if (filterApp && def.source_app_definition_id !== filterApp && def.target_app_definition_id !== filterApp) return false;
    if (filterTitle && !def.service_definition_title.toLowerCase().includes(filterTitle.toLowerCase())) return false;
    if (filterStatus === '1' && !def.is_enabled) return false;
    if (filterStatus === '0' && def.is_enabled) return false;
    return true;
  });

  const createServiceMutation = useMutation({
    mutationFn: (def: any) =>
      api.post('/api/services/add', {
        service_definition_id: def.service_definition_id,
        source_app_id: def.source_app_definition_id,
        target_app_id: def.target_app_definition_id,
        service_title: def.service_definition_title,
        service_description: def.service_definition_description,
      }),
    onSuccess: () => {
      toast.success('Service created successfully.');
      navigate('/services');
    },
    onError: () => {
      toast.error('Failed to create service.');
    },
  });

  function clearFilters() {
    setFilterApp('');
    setFilterTitle('');
    setFilterStatus('');
  }

  function showDetails(def: any) {
    setOffcanvasTitle(def.service_definition_title);
    setOffcanvasLink(def.documentation_link || '');
    setOffcanvasOpen(true);
  }

  return (
    <div>
      {/* Header — matches Wappler */}
      <div className="row mb-3">
        <h1 className="text-2xl font-bold">Hi {user?.firstname},</h1>
      </div>

      <div className="flex items-center justify-between mb-4">
        <p className="font-semibold text-[#767676]">
          If you can't find what you are looking for, then tell us about your connector requirements.
        </p>
        <Link to="/help/contact">
          <Button variant="secondary">Request a Connector</Button>
        </Link>
      </div>

      {/* Tabs — matches Wappler */}
      <ul className="nav nav-tabs mb-4 flex border-b border-gray-200">
        <li className="nav-item">
          <Link to="/services" className="nav-link px-4 py-2 text-[#767676] hover:text-[#cc0404] -mb-px border-b-2 border-transparent hover:border-[#cc0404]">
            Active Services
          </Link>
        </li>
        <li className="nav-item">
          <Link to="/services/archive" className="nav-link px-4 py-2 text-[#767676] hover:text-[#cc0404] -mb-px border-b-2 border-transparent hover:border-[#cc0404]">
            Archived Services
          </Link>
        </li>
        <li className="nav-item">
          <Link to="/services/catalog" className="nav-link px-4 py-2 text-[#cc0404] -mb-px border-b-2 border-[#cc0404] font-medium">
            Services Catalog
          </Link>
        </li>
      </ul>

      {/* Filters — matches Wappler layout: col-md-2 app, col-md-7 search, col-md-2 status, col-md-1 clear */}
      <div className="flex gap-3 mb-4 items-end">
        <div className="w-[16.66%]">
          <select
            className="form-select w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
            value={filterApp}
            onChange={(e) => setFilterApp(e.target.value)}
          >
            <option value="">Filter by App</option>
            {appDefinitions.map((app: any) => (
              <option key={app.app_definition_id} value={app.app_definition_id}>
                {app.app_name}
              </option>
            ))}
          </select>
        </div>
        <div className="w-[58.33%]">
          <Input
            type="text"
            placeholder="Search services list"
            value={filterTitle}
            onChange={(e) => setFilterTitle(e.target.value)}
            className="rounded-[4px] h-[42px]"
          />
        </div>
        <div className="w-[16.66%]">
          <select
            className="form-select w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">Filter by status</option>
            <option value="1">Available</option>
            <option value="0">Coming Soon</option>
          </select>
        </div>
        <div className="w-[8.33%] pt-2">
          <button className="text-[#cc0404] hover:underline text-sm" onClick={clearFilters}>
            Clear All
          </button>
        </div>
      </div>

      {/* Catalog cards — matches Wappler: row with col-md-6 cards */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading catalog...</div>
      ) : definitions.length === 0 ? (
        <p className="text-gray-500 py-4">No results found. Try a different search criteria.</p>
      ) : (
        <div className="flex flex-wrap -mx-2">
          {definitions.map((def: any) => (
            <div key={def.service_definition_id} className="w-full md:w-1/2 px-2 mb-4">
              <Card className="rounded-[20px]">
                <CardContent className="p-6">
                  {/* Title + Create Service button */}
                  <div className="flex justify-between items-start mb-4">
                    <h5 className="text-base font-semibold m-0">{def.service_definition_title}</h5>
                    <div>
                      {def.is_enabled ? (
                        <button
                          className="text-[#cc0404] hover:underline text-sm font-normal"
                          onClick={() => createServiceMutation.mutate(def)}
                          disabled={createServiceMutation.isPending}
                        >
                          Create Service <ChevronRight className="h-4 w-4 inline" />
                        </button>
                      ) : (
                        <span className="text-[#767676] text-sm">Coming Soon</span>
                      )}
                    </div>
                  </div>

                  {/* Service Description section — matches Wappler k-data-header */}
                  <div className="bg-gray-50 p-4 rounded-[4px] mb-0">
                    <strong className="text-sm block mb-1">Service Description</strong>
                    <div className="text-sm text-[#767676]">{def.service_definition_description}</div>
                    {def.documentation_link && (
                      <button
                        className="text-[#cc0404] hover:underline text-sm mt-1 p-0 bg-transparent border-none"
                        onClick={() => showDetails(def)}
                      >
                        See details
                      </button>
                    )}
                  </div>

                  {/* Card footer — matches Wappler: bg-light, app logos, credits */}
                  <div className="flex items-center justify-between bg-[#f8f9fa] p-3 rounded-b-[20px] -mx-6 -mb-6 mt-0 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      {def.source_app_logo && (
                        <img
                          src={def.source_app_logo}
                          alt={def.source_app_name || 'Source'}
                          width={48}
                          height={48}
                          className="rounded object-contain"
                        />
                      )}
                      {def.target_app_logo && (
                        <img
                          src={def.target_app_logo}
                          alt={def.target_app_name || 'Target'}
                          width={48}
                          height={48}
                          className="rounded object-contain"
                        />
                      )}
                    </div>
                    <div className="text-sm text-[#767676]">
                      Credit: {def.credit_consumption}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Offcanvas — matches Wappler: right side, 50% width, iframe for docs */}
      {offcanvasOpen && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/20" onClick={() => setOffcanvasOpen(false)} />
          <div className="fixed top-0 right-0 bottom-0 w-1/2 bg-white shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h5 className="text-lg font-semibold">{offcanvasTitle}</h5>
              <button
                className="p-2 hover:bg-gray-100 rounded"
                onClick={() => setOffcanvasOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="p-4">
              {offcanvasLink ? (
                <iframe
                  src={offcanvasLink}
                  className="w-full border-0"
                  style={{ height: '80vh' }}
                  title={offcanvasTitle}
                />
              ) : (
                <p className="text-[#767676]">No documentation available.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}